import { m, type Message } from '@/i18n';
import { meansApplyPlan } from '../flow-definition';
import { extractHashtags, foldTag, parseTagPath } from '../item-tags';
import { attributeLaneCommands, type LaneAttribute } from '../lane-attributes';
import { refsEqual } from '../refs';
import {
  EDGE_TARGET,
  edgeFactsFor,
  edgeRefusal,
  forwardEdge,
  laneEdge,
  lifecycleEdges,
  type EdgeFacts,
  type EdgeKind,
} from '../lifecycle-edges';
import { type Actor } from '../seats';
import type { MeansPair } from '../stage-gates';
import { domainError, type Command, type DomainError, type WorkItemState } from '../types';
import {
  cascadeFor,
  conclusionOf,
  deliveredAfter,
  planWithConclusionGuard,
  stayClause,
  sweptConsequences,
  type ParentReading,
} from './clarification';
import { planFrom } from './plan';
import { dispatchedCommands } from './shared';
import {
  PLAN_MAX_ITEMS,
  type Context,
  type Intent,
  type PlanStep,
  type Resolution,
  type Stimulus,
} from './types';

/**
 * **Step 4: the intent, one row per stimulus kind** (docs/RESOLUTION.md §1,
 * §2). What THIS stimulus means for THIS actor in THIS context: the tick table
 * of §2, the un-tick found through the origin, an act (a verb from a sheet or a
 * value on a lane), the cancellation, the stage move, and an event as a
 * stimulus. Each row produces the ONE statement and the commands it compiles
 * to, before the fold (step 6) and before permissions (step 5).
 *
 * Two neighbours sit with the rows that speak them: the statement text, and the
 * position change a delivery carries (§6, "flows ride the plan" - the
 * transition decorates the intent, and only these rows call for it).
 */

/**
 * The inverse of a plan's steps (§2's un-tick): "take this statement back".
 * Found through the origin marker, never a hand-written table - the steps are
 * reversed (undo the last act first, which also puts Reopen in front, the act
 * compiler's own law) and each command mapped to its opposite. A command with
 * no inverse refuses the whole reversal rather than guessing.
 *
 * **It spans every item the plan touched** (2026-09-22), because a tick now
 * sweeps: the steps carry their own `itemId`, and each is inverted against
 * THAT item's state (`stateOf`). An item whose state the caller could not
 * recover refuses the whole reversal - taking back half a statement is not
 * taking it back.
 */
export function inversePlan(
  steps: readonly PlanStep[],
  stateOf: (itemId: string) => WorkItemState | undefined,
  actor: Actor,
): { ok: true; steps: PlanStep[] } | { ok: false; error: DomainError } {
  const out: PlanStep[] = [];
  for (const step of [...steps].reverse()) {
    const state = stateOf(step.itemId);
    if (!state) {
      return {
        ok: false,
        error: domainError('invalid_state', m('resolution.refusal.partCannotBeTakenBack')),
      };
    }
    const inverse = inverseOf(step.command, state, actor);
    if (!inverse) {
      return {
        ok: false,
        error: domainError(
          'invalid_state',
          m('resolution.refusal.noInverse', { command: step.command.type }),
        ),
      };
    }
    out.push({ itemId: step.itemId, command: inverse });
  }
  return { ok: true, steps: out };
}

/** The steps of a reversal that fall on OTHER items, as a plan's cascade:
 *  one entry per item, in the order the inverse first reaches it. */
function cascadeOfSteps(steps: readonly PlanStep[]): Intent['cascade'] {
  const byItem = new Map<string, Command[]>();
  for (const step of steps) {
    const held = byItem.get(step.itemId);
    if (held) held.push(step.command);
    else byItem.set(step.itemId, [step.command]);
  }
  return [...byItem].map(([itemId, commands]) => ({ itemId, commands }));
}

function inverseOf(command: Command, state: WorkItemState, actor: Actor): Command | null {
  switch (command.type) {
    case 'Tender':
      return { type: 'Retract' };
    case 'Retract':
      return { type: 'Tender' };
    case 'Close':
      return { type: 'Reopen' };
    case 'Reopen':
      return { type: 'Close' };
    case 'SetActive':
      return { type: 'SetActive', active: !command.active };
    case 'Claim':
      // Stepping back out of the seat the claim took. The seat set is the
      // projection's, minus this actor's own ref: a dedupe, not an authority
      // question, so it reads the refs directly.
      return {
        type: 'Reassign',
        role: 'contributor',
        // eslint-disable-next-line no-restricted-syntax -- seat-set diff for the inverse of Claim; no authority question here
        to: state.contributors.filter((r) => !refsEqual(r, actor.ref)),
      };
    case 'Accept':
    case 'Dispute':
      return { type: 'Resolve' };
    // A tag is a set member: applying and removing it are each other's undo.
    case 'Tag':
      return { type: 'Untag', tag: command.tag };
    case 'Untag':
      return { type: 'Tag', tag: command.tag };
    default:
      // Resolve (the prior verdict is not in the step), the field setters, a
      // comment: nothing the machine may invert from the command alone.
      return null;
  }
}

/**
 * **The tick is the FORWARD EDGE** (docs/RESOLUTION.md §2, re-cut by Leon
 * 2026-11-03 with the finishing lifecycle, docs/CLOSED_AXIS.md "Finishing").
 * A tick says "I am done", and what that means here is the next step of the
 * item's position for this actor, read off the one edge table
 * (`domain/lifecycle-edges.ts`): Done on an open item, Ready for review on an
 * open item under a review duty, Accept on an item in review for its
 * reviewer. Nothing else: an item in review is not a viewer's to tick unless
 * they hold the review, and a concluded item is taken back by the un-tick,
 * never by a second tick.
 *
 * The seatless tick no longer seats its tapper first (2026-11-03): the actor
 * is on the event, and an out-of-role act is a flag on its level, never a
 * different act.
 *
 * Done and Accept SWEEP what is the person's own below (`tickSweep`), every
 * edge that hands work in carries its flow's position change (`transitionOf`),
 * and Ready for review says what the item waits on, for the ticked line's
 * note.
 *
 * **No tick accepts in passing**: the machine never takes a verdict on a tick
 * except where the tick IS the reviewer's Accept, on an item already in
 * review. A view that AUTHORS its tick's meaning (§5's `tickMeans`, direction
 * rather than settled) speaks its own word instead.
 */
export function resolveTick(
  stimulus: Extract<Stimulus, { kind: 'tick' }>,
  context: Context,
): Resolution {
  const { item } = context;
  const itemId = stimulus.itemId;

  const authored = context.grammar?.tickMeans;
  if (authored) {
    // §5: the grammar is data. A lens whose tick means `accept` gives the
    // review checklist without a new rail; the edge table does not run.
    const pair: MeansPair =
      authored === 'deliver'
        ? { attr: 'delivered', value: true }
        : authored === 'close'
          ? { attr: 'closed', value: true }
          : { attr: 'verdict', value: 'accepted' };
    const commands = dispatchedCommands(meansApplyPlan([pair], item, 'flow:self'));
    const statement = m(
      authored === 'deliver'
        ? 'resolution.statement.readyForReview'
        : authored === 'close'
          ? 'resolution.statement.done'
          : 'resolution.statement.accepted',
    );
    const intent: Intent = { kind: 'tick', itemId, statement, commands };
    // An authored `close` concludes, so it sweeps exactly as Done does - and
    // asks nothing, because no tick asks (§2). It delivers nothing, so its
    // lines take the item's own delivery.
    if (authored !== 'close') return planFrom(stimulus, context, intent);
    const swept = tickSweep(context, itemId, {
      delivered: deliveredAfter(item.delivered, commands),
    });
    if (!swept.ok) return { kind: 'refused', error: swept.error };
    return planFrom(
      stimulus,
      context,
      { ...intent, ...(swept.cascade?.length ? { cascade: swept.cascade } : {}) },
      { consequences: swept.consequences },
    );
  }

  const facts = edgeFactsOf(context);
  if (facts.position === 'done' || facts.position === 'cancelled') {
    // Already ended: the line is ticked and gone. Taking it back is the
    // un-tick's job, not a second tick's.
    return {
      kind: 'refused',
      error: domainError('no_change', m('resolution.refusal.alreadyEnded')),
    };
  }
  const forward = forwardEdge(lifecycleEdges(facts));
  if (!forward || forward.kind === 'reopen' || forward.kind === 'cancel') {
    // In review, and not the viewer's to review: the line waits on the
    // reviewer, and a tick has nothing to add.
    return {
      kind: 'refused',
      error: domainError('no_change', m('resolution.refusal.waitsOnReview')),
    };
  }
  return planEdge(forward.kind, stimulus, context, { kind: 'tick' });
}

/**
 * **One edge of the finishing lifecycle** (docs/CLOSED_AXIS.md "Finishing",
 * Leon 2026-11-03). Which edges stand here is the edge table's
 * (`lifecycleEdges`); an edge that does not stand is refused in the table's
 * own words (`edgeRefusal`), so an agent's verb and a menu row refuse alike.
 */
export function resolveEdge(
  stimulus: Extract<Stimulus, { kind: 'edge' }>,
  context: Context,
): Resolution {
  const facts = edgeFactsOf(context);
  if (!lifecycleEdges(facts).some((e) => e.kind === stimulus.edge)) {
    return { kind: 'refused', error: edgeRefusal(stimulus.edge, facts) };
  }
  return planEdge(stimulus.edge, stimulus, context, {
    kind: 'edge',
    ...(stimulus.note !== undefined ? { note: stimulus.note } : {}),
    ...(stimulus.reason !== undefined ? { reason: stimulus.reason } : {}),
  });
}

/** The edge table's facts over a resolver context. */
export function edgeFactsOf(context: Context): EdgeFacts {
  return edgeFactsFor(context.actor, context.item, {
    reviewRequired: context.reviewRequired,
    reviewers: context.reviewers,
    seatOffered: context.seatOffered,
    deliveryCovered: context.deliveryCovered,
  });
}

/** The pairs Reopen applies: back to open, the handover and the verdict
 *  cleared (2026-11-03: open is always the same open). */
const REOPEN_PAIRS: readonly MeansPair[] = [
  { attr: 'closed', value: false },
  { attr: 'delivered', value: false },
  { attr: 'verdict', value: 'none' },
];

/**
 * **What each edge DOES**: one plan per edge, the steps through `decide`.
 *
 * - **Done**: Tender, then Close, and the sweep of the person's own open
 *   lines (§6, as a tick's always was).
 * - **Ready for review**: Tender alone; the item waits on the review.
 * - **Accept**: the verdict accepted, then Close, and the same sweep: the
 *   item ends done.
 * - **Deny**: the verdict denied (with its reason), then Retract: the item is
 *   open again, wearing the Denied record.
 * - **Reopen**: Reopen where closed, Retract where delivered, Resolve where a
 *   verdict stands, through the one act compiler.
 *
 * Every edge that hands work in or ends it carries its flow's position change
 * when the stage's `means` hold over the state it leaves (`transitionOf`).
 */
function planEdge(
  kind: Exclude<EdgeKind, 'cancel'>,
  stimulus: Stimulus,
  context: Context,
  payload: { kind: 'tick' | 'edge' | 'attribute'; note?: string; reason?: string },
): Resolution {
  const { item } = context;
  const itemId = stimulus.itemId;
  const intentKind = payload.kind;
  const verdictAfterTender = item.delivered ? item.verdict : 'open';
  switch (kind) {
    case 'done': {
      const commands: Command[] = [];
      if (!item.delivered) commands.push({ type: 'Tender' });
      commands.push({ type: 'Close' });
      // Done ends delivered and closed, so what it sweeps reads done too.
      const swept = tickSweep(context, itemId, { delivered: true });
      if (!swept.ok) return { kind: 'refused', error: swept.error };
      const intent: Intent = {
        kind: intentKind,
        itemId,
        statement: m('resolution.statement.done'),
        commands,
        ...(swept.cascade?.length ? { cascade: swept.cascade } : {}),
        ...transitionOf(context, {
          ...item,
          delivered: true,
          closed: true,
          verdict: verdictAfterTender,
        }),
      };
      return planFrom(stimulus, context, intent, { consequences: swept.consequences });
    }
    case 'ready': {
      const intent: Intent = {
        kind: intentKind,
        itemId,
        statement: m('resolution.statement.readyForReview'),
        commands: [{ type: 'Tender' }],
        waits: { on: 'review' },
        ...transitionOf(context, { ...item, delivered: true, verdict: verdictAfterTender }),
      };
      return planFrom(stimulus, context, intent);
    }
    case 'accept': {
      const note = payload.note?.trim();
      const commands: Command[] = [
        note ? { type: 'Accept', note } : { type: 'Accept' },
        { type: 'Close' },
      ];
      const swept = tickSweep(context, itemId, { delivered: true });
      if (!swept.ok) return { kind: 'refused', error: swept.error };
      const intent: Intent = {
        kind: intentKind,
        itemId,
        statement: m('resolution.statement.acceptedAndDone'),
        commands,
        ...(swept.cascade?.length ? { cascade: swept.cascade } : {}),
        ...transitionOf(context, { ...item, closed: true, verdict: 'accepted' }),
      };
      return planFrom(stimulus, context, intent, { consequences: swept.consequences });
    }
    case 'deny': {
      const reason = payload.reason?.trim();
      const intent: Intent = {
        kind: intentKind,
        itemId,
        statement: m('resolution.statement.denied'),
        commands: [reason ? { type: 'Dispute', reason } : { type: 'Dispute' }, { type: 'Retract' }],
      };
      return planFrom(stimulus, context, intent);
    }
    case 'reopen': {
      const intent: Intent = {
        kind: intentKind,
        itemId,
        statement: m('resolution.statement.reopened'),
        commands: dispatchedCommands(meansApplyPlan(REOPEN_PAIRS, item, 'flow:self')),
      };
      return planFrom(stimulus, context, intent);
    }
  }
}

/**
 * **A tick SWEEPS, and never forces** (§2, §6; ruled by Leon 2026-09-22). A
 * tick on a checklist category that closed the category and left its lines
 * open is not what "I am done" means, so a tick that closes carries the same
 * cascade the question's "Close them too" builds: every open descendant that
 * is the person's ALONE, deepest first, in the one transaction.
 *
 * **The set does not depend on the tapper's seat** (2026-09-27, when a doer's
 * tick began to close): `conclusionOf` decides it from the children's own
 * `canAct`, their ties and their depth, so a doer's tick on a category sweeps
 * exactly the lines an owner's would. One rule for one line is the same rule
 * for the set.
 *
 * **The swept lines take the tick's reading** (Leon, 2026-09-28: "read as
 * one"): a tick ends delivered and closed, so each line it sweeps is delivered
 * and closed too and reads done, never cancelled (`cascadeFor`). The un-tick's
 * inverse takes each line's `Tender` back with its `Close`.
 *
 * It never takes a TIED child - work another open list holds, or open work
 * outside this item depends on. That is a judgment call, and a tick asks
 * nothing, so only the explicit conclusion's third answer may make it. The way
 * back is the un-tick, which inverts the whole plan through the origin marker
 * (§8) - the undo that makes a sweep safe without a prompt.
 *
 * With no subtree gathered (a surface that speaks the tick but cannot sweep)
 * the item closes alone and says what it leaves, as it did before.
 */
function tickSweep(
  context: Context,
  targetId: string,
  reading: ParentReading,
):
  | { ok: true; cascade: Intent['cascade']; consequences: Message[] }
  | { ok: false; error: DomainError } {
  const open = context.weight.openChildren;
  if (open === 0) return { ok: true, cascade: [], consequences: [] };
  if (!context.children) return { ok: true, cascade: [], consequences: [stayClause(open)] };

  const conclusion = conclusionOf(context.children, context.hiddenChildren ?? 0, targetId);
  if (conclusion.safe.length + 1 > PLAN_MAX_ITEMS) {
    return {
      ok: false,
      error: domainError(
        'invalid_input',
        m('resolution.refusal.tooLarge', { count: conclusion.safe.length + 1 }),
      ),
    };
  }
  return {
    ok: true,
    cascade: cascadeFor(conclusion.safe, reading),
    consequences: sweptConsequences(
      conclusion.safe.length,
      conclusion.total - conclusion.safe.length,
    ),
  };
}

/**
 * §2's un-tick: the inverse of the original plan, found through the origin -
 * **the whole plan, sweep included** (2026-09-22). A tick that swept a
 * category's lines is taken back with them, which is the undo that lets the
 * sweep happen without a prompt at all.
 *
 * Every item the plan touched besides the target must still be the actor's to
 * act on; one that is not refuses the whole reversal rather than reopening
 * part of a statement.
 */
export function resolveUntick(
  stimulus: Extract<Stimulus, { kind: 'untick' }>,
  context: Context,
): Resolution {
  if (!context.origin || !context.originSteps || context.originSteps.length === 0) {
    return {
      kind: 'refused',
      error: domainError('not_found', m('resolution.refusal.nothingToTakeBack')),
    };
  }
  const target = stimulus.itemId;
  const others = new Map((context.originItems ?? []).map((item) => [item.itemId, item]));
  for (const step of context.originSteps) {
    if (step.itemId === target || others.get(step.itemId)?.canAct) continue;
    return {
      kind: 'refused',
      error: domainError('forbidden', m('resolution.refusal.notYoursToTakeBack')),
    };
  }
  const inverse = inversePlan(
    context.originSteps,
    (itemId) => (itemId === target ? context.item : others.get(itemId)?.state),
    context.actor,
  );
  if (!inverse.ok) return { kind: 'refused', error: inverse.error };

  const cascade = cascadeOfSteps(inverse.steps.filter((s) => s.itemId !== target));
  const intent: Intent = {
    kind: 'untick',
    itemId: target,
    statement: m('resolution.statement.takenBack'),
    commands: inverse.steps.filter((s) => s.itemId === target).map((s) => s.command),
    ...(cascade?.length ? { cascade } : {}),
  };
  return planFrom(stimulus, context, intent);
}

/** A verb from an act sheet, or a value picked on the line / dropped on a lane. */
export function resolveAct(
  stimulus: Extract<Stimulus, { kind: 'act' }>,
  context: Context,
): Resolution {
  const itemId = stimulus.itemId;
  if ('command' in stimulus.target) {
    const command = stimulus.target.command;
    const intent: Intent = {
      kind: 'command',
      itemId,
      statement: commandStatement(command),
      commands: [command],
    };
    return command.type === 'Close'
      ? planWithConclusionGuard(stimulus, context, intent)
      : planFrom(stimulus, context, intent);
  }

  const { attribute, value } = stimulus.target;
  // The lifecycle axes are the edge table's (2026-11-03): a value picked on
  // the line or a lane dropped into is the edge that lands there, refused in
  // the table's own words where it does not stand for this actor.
  if (attribute !== 'active') {
    const facts = edgeFactsOf(context);
    const kind = laneEdge(attribute, value, lifecycleEdges(facts));
    if (kind === null) {
      return {
        kind: 'refused',
        error: domainError('invalid_input', m('resolution.refusal.verdictIsRecord')),
      };
    }
    if (EDGE_TARGET[kind] === facts.position) {
      return {
        kind: 'refused',
        error: domainError('no_change', m('resolution.refusal.alreadyThere')),
      };
    }
    if (!lifecycleEdges(facts).some((e) => e.kind === kind)) {
      return { kind: 'refused', error: edgeRefusal(kind, facts) };
    }
    return kind === 'cancel'
      ? resolveCancel({ ...stimulus, kind: 'cancel' }, context, 'attribute')
      : planEdge(kind, stimulus, context, { kind: 'attribute' });
  }
  const commands = attributeLaneCommands(attribute, value, context.item);
  if (commands.length === 0) {
    return {
      kind: 'refused',
      error: domainError('no_change', m('resolution.refusal.alreadyThere')),
    };
  }
  const intent: Intent = {
    kind: 'attribute',
    itemId,
    statement: attributeStatement(attribute, value),
    commands,
  };
  return planFrom(stimulus, context, intent);
}

/**
 * A title typed with `#word` tokens (docs/FIELDS_AND_TAGS.md §6.3, "entry is
 * typing"): the stripped title and one `Tag` per token, one statement in one
 * transaction. A token already worn is a benign no-op the fold skips. A title
 * that would be empty without its tags is refused.
 *
 * Only a title that CARRIES a token comes here; a plain rename stays a
 * `SetTitle` outside the resolver. An un-tick skips this plan either way: it
 * takes back the newest TICK plan (`untickOrigin`), and `SetTitle` has no
 * inverse.
 */
export function resolveRetitle(
  stimulus: Extract<Stimulus, { kind: 'retitle' }>,
  context: Context,
): Resolution {
  const { text, tags } = extractHashtags(stimulus.text);
  if (text.length === 0) {
    return {
      kind: 'refused',
      error: domainError('invalid_input', m('resolution.refusal.titleNeeded')),
    };
  }
  const renamed = text !== context.item.title;
  const commands: Command[] = [
    ...(renamed ? [{ type: 'SetTitle', text } as const] : []),
    ...tags.map((tag) => ({ type: 'Tag', tag }) as const),
  ];
  // The statement names only what the plan changes: a token the item already
  // wears is a no-op the fold skips.
  const worn = new Set(context.item.tagFolds ?? []);
  const fresh = tags.filter((t) => !worn.has(foldTag(t)));
  const tagged = fresh.map((t) => `#${t}`).join(' ');
  const intent: Intent = {
    kind: 'retitle',
    itemId: stimulus.itemId,
    statement:
      fresh.length === 0
        ? m('resolution.statement.renamed')
        : renamed
          ? m('resolution.statement.renamedAndTagged', { tags: tagged })
          : m('resolution.statement.tagged', { tags: tagged }),
    commands,
  };
  return planFrom(stimulus, context, intent);
}

/**
 * One worn tag edited into another on this item (docs/FIELDS_AND_TAGS.md §6.3):
 * the chip opens its own path, and making it more specific (`einkaufen` ->
 * `einkaufen/obst`) is the common edit. An Untag and a Tag under one statement,
 * so the feed speaks one change and one undo takes both back.
 *
 * Equal folds are `no_change` (a respelling is not an edit here; the
 * multi-item rename owns spelling). A `from` the item does not wear refuses,
 * as does a `to` that is not a tag. A `to` the item already wears folds its
 * Tag away, and the edit is then the Untag alone - the same end state.
 */
export function resolveRetag(
  stimulus: Extract<Stimulus, { kind: 'retag' }>,
  context: Context,
): Resolution {
  const from = parseTagPath(stimulus.from);
  if (!from.ok) return { kind: 'refused', error: from.error };
  const to = parseTagPath(stimulus.to);
  if (!to.ok) return { kind: 'refused', error: to.error };
  if (from.value.fold === to.value.fold) {
    return {
      kind: 'refused',
      error: domainError('no_change', m('resolution.refusal.tagUnchanged')),
    };
  }
  if (!(context.item.tagFolds ?? []).includes(from.value.fold)) {
    return {
      kind: 'refused',
      error: domainError('not_found', m('resolution.refusal.notTagged', { tag: from.value.tag })),
    };
  }
  const intent: Intent = {
    kind: 'retag',
    itemId: stimulus.itemId,
    statement: m('resolution.statement.retagged', { from: from.value.tag, to: to.value.tag }),
    commands: [
      { type: 'Untag', tag: from.value.tag },
      { type: 'Tag', tag: to.value.tag },
    ],
  };
  return planFrom(stimulus, context, intent);
}

/** The pairs a cancellation applies (docs/CLOSED_AXIS.md): concluded, with
 *  the delivery and any verdict cleared, so the item reads CANCELLED and not
 *  done, and wears no record of a review it no longer has. */
const CANCEL_PAIRS: readonly MeansPair[] = [
  { attr: 'delivered', value: false },
  { attr: 'verdict', value: 'none' },
  { attr: 'closed', value: true },
];

/**
 * The way out (docs/CLOSED_AXIS.md, "the way down"): the Cancel edge, from
 * open or from review (the edge table). Retract where a delivery stands,
 * Resolve where a verdict stands, then Close - compiled through the one act
 * compiler, like every other pair set, so the ordering laws are not restated
 * here. The platform asks for no reason at the cancel (Leon, 2026-09-21);
 * `Reopen` is the undo.
 *
 * It concludes, so it meets §6's guard exactly as a Close does: cancelling a
 * parent with open real children asks the one-line question first.
 */
export function resolveCancel(
  stimulus: Extract<Stimulus, { kind: 'cancel' }> | Extract<Stimulus, { kind: 'act' }>,
  context: Context,
  intentKind: 'cancel' | 'attribute' = 'cancel',
): Resolution {
  const facts = edgeFactsOf(context);
  if (facts.position === 'done' || facts.position === 'cancelled') {
    return { kind: 'refused', error: edgeRefusal('cancel', facts) };
  }
  const reason = stimulus.kind === 'cancel' ? stimulus.reason : undefined;
  const commands = dispatchedCommands(
    meansApplyPlan(CANCEL_PAIRS, context.item, 'flow:self', reason),
  );
  const intent: Intent = {
    kind: intentKind,
    itemId: stimulus.itemId,
    statement: m('resolution.statement.cancelled'),
    commands,
  };
  return planWithConclusionGuard(stimulus, context, intent);
}

/** A stage move (docs/FLOW.md): the target stage's `means` applied as real
 *  acts, plus the position change. Legality is the gate's, decided by
 *  `decideAdvanceMember` in the context builder. */
export function resolveMove(
  stimulus: Extract<Stimulus, { kind: 'move' }>,
  context: Context,
): Resolution {
  const membership = context.memberships.find((m) => m.flowId === stimulus.flowId);
  if (!membership) {
    return {
      kind: 'refused',
      error: domainError('not_found', m('resolution.refusal.notAMember')),
    };
  }
  if (!membership.target || membership.target.key !== stimulus.toStageKey) {
    return {
      kind: 'refused',
      error: domainError(
        'not_found',
        m('resolution.refusal.noSuchStage', { stage: stimulus.toStageKey }),
      ),
    };
  }
  if (!membership.gate.allows) {
    return {
      kind: 'refused',
      // The gate's own reason is what the boundary still waits on
      // (domain/flows.ts), a descriptor like the rest.
      error: domainError('forbidden', membership.gate.reason ?? m('resolution.refusal.gateClosed')),
    };
  }
  const commands = dispatchedCommands(
    meansApplyPlan(membership.target.means, context.item, `flow:${membership.flowId}`),
  );
  const intent: Intent = {
    kind: 'move',
    itemId: stimulus.itemId,
    statement: m('resolution.statement.moved', { stage: membership.target.label }),
    commands,
    transition: { flowId: stimulus.flowId, toStageKey: stimulus.toStageKey },
  };
  // A move whose stage applies no act is still a real position change, so the
  // empty command list is not a refusal here (unlike every other row).
  return planFrom(stimulus, context, intent, { allowEmptySteps: true });
}

/**
 * **A tick on a flow member is also a position change** (§6: "otherwise the
 * board and the checklist would tell two truths"). The stage the delivery
 * draws the item into is gathered by the context builder as the membership's
 * `target` - the first stage after the member's own position whose `means`
 * name `delivered: true` - together with the gate verdict for it. This adds
 * the transition to the plan when three things hold: the gate passes, the
 * target is not where the item already sits, and the stage's `means` actually
 * hold over the state the tick leaves.
 *
 * A gate that does not pass drops the transition and keeps the tick: "I am
 * done" is always true (§2), and a closed gate is a fact about the flow, not
 * about the person's own part.
 */
function transitionOf(
  context: Context,
  after: Pick<WorkItemState, 'delivered' | 'closed' | 'active' | 'verdict'>,
): { transition?: { flowId: string; toStageKey: string } } {
  for (const membership of context.memberships) {
    const target = membership.target;
    if (!target || target.key === membership.stageKey) continue;
    if (!membership.gate.allows) continue;
    if (!meansHoldOverAtoms(target.means, after)) continue;
    return { transition: { flowId: membership.flowId, toStageKey: target.key } };
  }
  return {};
}

/**
 * Whether a stage's `means` hold over the atoms and the verdict a plan leaves.
 * Only those four are decidable here: a stage whose `means` also name a TAG or
 * a FIELD is left to the automation engine's edge-triggered pull
 * (docs/FLOW_DEFINITION.md §8b), which reads the whole state vector. Pure, and
 * conservative on purpose - an undecidable pair means the tick does not move
 * the position, never that it moves it wrongly.
 */
function meansHoldOverAtoms(
  means: readonly MeansPair[],
  after: Pick<WorkItemState, 'delivered' | 'closed' | 'active' | 'verdict'>,
): boolean {
  if (means.length === 0) return false;
  return means.every((pair) => {
    switch (pair.attr) {
      case 'delivered':
        return after.delivered === pair.value;
      case 'closed':
        return after.closed === pair.value;
      case 'active':
        return after.active === pair.value;
      case 'verdict':
        return (after.verdict === 'open' ? 'none' : after.verdict) === pair.value;
      default:
        return false;
    }
  });
}

function commandStatement(command: Command): Message {
  switch (command.type) {
    case 'Tender':
      return m('resolution.statement.delivered');
    case 'Retract':
      return m('resolution.statement.deliveryTakenBack');
    case 'Close':
      return m('resolution.statement.closed');
    case 'Reopen':
      return m('resolution.statement.reopened');
    case 'Claim':
      return m('resolution.statement.seated');
    case 'Accept':
      return m('resolution.statement.accepted');
    case 'Dispute':
      return m('resolution.statement.denied');
    case 'Resolve':
      return m('resolution.statement.verdictWithdrawn');
    case 'SetActive':
      return m(
        command.active ? 'resolution.statement.markedActive' : 'resolution.statement.markedIdle',
      );
    case 'Comment':
      return m('resolution.statement.commented');
    case 'Acknowledge':
      return m('resolution.statement.acknowledged');
    case 'Reassign':
      return m(
        command.role === 'requester'
          ? 'resolution.statement.ownerSet'
          : 'resolution.statement.doerSet',
      );
    case 'SetReviewRequired':
      return m(
        command.required
          ? 'resolution.statement.reviewRequired'
          : 'resolution.statement.reviewNotRequired',
      );
    case 'Tag':
      return m('resolution.statement.tagged', { tags: `#${tagText(command.tag)}` });
    case 'Untag':
      return m('resolution.statement.untagged', { tag: tagText(command.tag) });
    default:
      return m('resolution.statement.changed');
  }
}

/** A typed tag as a statement names it: without the `#` the person may have typed. */
function tagText(tag: string): string {
  const t = tag.trim();
  return t.startsWith('#') ? t.slice(1) : t;
}

function attributeStatement(attribute: LaneAttribute, value: string): Message {
  switch (attribute) {
    case 'delivered':
      return m(
        value === 'yes'
          ? 'resolution.statement.delivered'
          : 'resolution.statement.deliveryTakenBack',
      );
    case 'closed':
      return m(value === 'yes' ? 'resolution.statement.closed' : 'resolution.statement.reopened');
    case 'active':
      return m(
        value === 'yes' ? 'resolution.statement.markedActive' : 'resolution.statement.markedIdle',
      );
    case 'verdict':
      return m(
        value === 'accepted'
          ? 'resolution.statement.accepted'
          : value === 'disputed'
            ? 'resolution.statement.denied'
            : 'resolution.statement.verdictWithdrawn',
      );
  }
}
